import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Camera, CloudSun, Butterfly, Compass, Warning, WarningCircle, Sparkle, X,
} from "@phosphor-icons/react";
import AddObservation from "@/components/AddObservation";
import { useAuth } from "@/contexts/AuthContext";
import LoginButton from "@/components/LoginButton";

// "What's Happening Around You?" — the top-level entry point to the
// NaturePulse Observation Network. Six CTAs open the multi-step submission
// form pre-set to a category kind. Registration is required.

const CTAS = [
  { key: "any", label: "Add observation", Icon: Plus, defaultKind: null, tone: "brand" },
  { key: "photos", label: "Add photos", Icon: Camera, defaultKind: null },
  { key: "weather", label: "Report weather", Icon: CloudSun, defaultKind: "weather" },
  { key: "wildlife", label: "Report wildlife", Icon: Butterfly, defaultKind: "nature" },
  { key: "road", label: "Report road condition", Icon: Compass, defaultKind: "road" },
  { key: "event", label: "Report natural event", Icon: Warning, defaultKind: "event" },
  { key: "incident", label: "Report incident", Icon: WarningCircle, defaultKind: "incident" },
];

export default function WhatsHappening({ variant = "hero" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [seedKind, setSeedKind] = useState(null);
  const [showLoginNudge, setShowLoginNudge] = useState(false);

  const openWith = (kind) => {
    if (!user) { setShowLoginNudge(true); return; }
    setSeedKind(kind); setOpen(true);
  };

  return (
    <section
      data-testid="whats-happening"
      className={`relative rounded-2xl border border-black/10 bg-gradient-to-br from-[#151312] to-[#241a11] text-[#F5EBDC] overflow-hidden ${
        variant === "hero" ? "p-6 md:p-10" : "p-5"
      }`}
    >
      <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#E4A15C] mb-3 flex items-center gap-2">
        <Sparkle size={12} weight="fill" /> Observation network · live
      </div>
      <h2 className={`font-serif-display leading-[1.02] tracking-tight ${
        variant === "hero" ? "text-4xl md:text-6xl" : "text-3xl md:text-4xl"
      }`}>
        What&rsquo;s happening around you?
      </h2>
      <p className="mt-3 md:mt-4 font-serif-display text-base md:text-lg text-[#E5D8C4]/80 max-w-2xl">
        See something interesting in nature, weather, wildlife or your surroundings? Photograph it, identify it, record where and when you saw it, and share your observation with the community.
      </p>

      <div className="mt-5 md:mt-6 flex flex-wrap gap-2">
        {CTAS.map(({ key, label, Icon, defaultKind, tone }) => (
          <button
            key={key}
            onClick={() => openWith(defaultKind)}
            data-testid={`wh-cta-${key}`}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase transition-colors ${
              tone === "brand"
                ? "bg-[#E4A15C] text-[#151312] hover:brightness-110"
                : "border border-[#F5EBDC]/30 text-[#F5EBDC] hover:bg-[#F5EBDC]/10"
            }`}
          >
            <Icon size={13} weight={tone === "brand" ? "bold" : "fill"} /> {label}
          </button>
        ))}
      </div>

      {!user && (
        <div className="mt-4 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#E5D8C4]/60">
          Sign in to submit observations
        </div>
      )}

      {/* Login nudge */}
      <AnimatePresence>
        {showLoginNudge && (
          <>
            <motion.div className="fixed inset-0 z-[70] bg-black/60" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={() => setShowLoginNudge(false)} />
            <motion.div
              className="fixed z-[71] inset-x-4 md:inset-x-auto md:right-8 top-1/2 md:top-24 -translate-y-1/2 md:translate-y-0 md:w-[420px] rounded-2xl bg-[#F5EBDC] text-[#151312] p-5 shadow-2xl"
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">Sign in required</div>
                  <div className="font-serif-display text-2xl mt-1 leading-tight">One identity across NaturePulse.</div>
                  <p className="text-sm mt-2 text-[#3A3830]">
                    Submitting an observation ties it to your NaturePulse account so you can edit it, follow the discussion, earn contributor stats and see it in your Nature Diary.
                  </p>
                </div>
                <button onClick={() => setShowLoginNudge(false)}><X size={16} weight="bold" /></button>
              </div>
              <div className="mt-4">
                <LoginButton label="Sign in to submit" className="border-[#151312] text-[#151312] hover:bg-[#151312] hover:text-[#F5EBDC]" />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {open && (
        <AddObservation
          seedKind={seedKind}
          onClose={() => setOpen(false)}
          onSubmitted={() => setOpen(false)}
        />
      )}
    </section>
  );
}
